import type * as React from "react";

import { cn } from "../lib/cn";

/**
 * `span` (the default) is the multi-fact row and is phrasing content, so it is
 * legal inside a pressable card's `<button>`. `time` is the one-fact footer,
 * where the fact is a timestamp and the element should say so.
 */
export type FilingLineAs = "span" | "time";

/*
 * Mono, 11px, annotation ink. The face is what makes a filing line read as
 * something stamped on the paper rather than written on it.
 */
const INK = "font-mono text-fine-print text-annotation";

/*
 * A column gap and no row gap, deliberately. This line wraps under real data -
 * a due date, a raised date, an assignee, a room code - and on ruled paper the
 * next line has to land on the next rule. A row gap would drop it into the
 * space between two rules, and off the ruling everything below it goes with it.
 * On unruled paper the same choice keeps a wrapped filing line reading as one
 * block of small print rather than two stacked lines.
 */
const ROW = "flex flex-wrap items-center gap-x-3";

export interface FilingLineProps extends React.HTMLAttributes<HTMLElement> {
  /** `span` (default) for the wrapping row of facts, `time` for a lone timestamp. */
  as?: FilingLineAs;
  /** Machine-readable timestamp. Only meaningful with `as="time"`. */
  dateTime?: string;
  /**
   * The facts, in filing order. Each one its own element, so the row can wrap
   * between them - not a single pre-joined string.
   */
  children: React.ReactNode;
  ref?: React.Ref<HTMLElement>;
}

/**
 * The small print along the bottom of a card: when it was raised, when it is
 * due, who has it, where it stands. Never the point of the card - it is the
 * stamp in the corner of the page, read only when someone goes looking.
 *
 * Two shapes, and which one you get follows from `as`. A lone timestamp is a
 * `<time>` and simply sits on its own line. Two or more facts are a wrapping
 * row separated by a column gap.
 *
 * No margin is baked in: a filing line sits under whatever the card wrote above
 * it, and how far under is the card's business (`mt-2` on a panel, `mt-auto
 * pt-2` on a note that has to push it to the bottom edge).
 */
export function FilingLine({
  as = "span",
  dateTime,
  children,
  className,
  ...rest
}: FilingLineProps) {
  if (as === "time")
    return (
      <time
        dateTime={dateTime}
        className={cn("block", INK, className)}
        // HTMLTimeElement adds `dateTime` to HTMLElement, so a ref typed for
        // the general case needs narrowing to land on this one.
        {...(rest as React.HTMLAttributes<HTMLTimeElement> & {
          ref?: React.Ref<HTMLTimeElement>;
        })}
      >
        {children}
      </time>
    );

  return (
    <span className={cn(ROW, INK, className)} {...rest}>
      {children}
    </span>
  );
}
